import {
  FaroErrorBoundary,
  withFaroRouterInstrumentation,
} from "@grafana/faro-react";
import * as Sentry from "@sentry/react";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { FlagProvider, useFlagsStatus } from "@unleash/proxy-client-react";
import * as React from "react";
import { I18nProvider } from "react-aria-components";
import { createRoot } from "react-dom/client";
import {
  type NavigateOptions,
  type RouteObject,
  createBrowserRouter,
  createHashRouter,
} from "react-router";
import { RouterProvider } from "react-router/dom";
import { TimeoutWatcher } from "@/auto-converted/lib/TimeoutWatcher";
import { FilterProvider } from "@/context/FilterContext";
import { ActivityLogsProvider } from "@/stores/ActivityLogs";
import { UpdateTasksProvider } from "@/stores/CvUpdateTasks";
import { ExtraInfoProvider } from "@/stores/ExtraInfos";
import { NavigationBlocker } from "@/stores/NetworkState";
import { SaveFailedProvider } from "@/stores/SaveFailed";
import { SelectedForMergeProvider } from "@/stores/SelectedForMerge";
import type { Router } from "@/types";
import { QueryClientProvider } from "@/util/fetch";
import { getCvPartner } from "@/util/getCvPartner";
import { invariant } from "@/util/invariant";
import { I18n } from "@/util/translations";

declare module "react-aria-components" {
  interface RouterConfig {
    routerOptions: NavigateOptions;
  }
}

const NothingUntilUnleashReady: React.FC<{
  children: React.ReactElement;
}> = ({ children }) => {
  const status = useFlagsStatus();

  if (status.flagsReady || status.flagsError) {
    return children;
  }

  return null;
};

const isDevelopment = process.env.NODE_ENV === "development";

const languages: Record<string, string> = {
  dk: "da-DK",
  de: "de-DE",
  en: "en-GB",
  fi: "fi-FI",
  fr: "fr-FR",
  no: "nb-NO",
  se: "sv-SE",
};

const locale = languages[I18n.locale] ?? I18n.locale;

const Providers: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ExtraInfoProvider>
    <SelectedForMergeProvider>
      <UpdateTasksProvider>
        <ActivityLogsProvider>
          <SaveFailedProvider>
            <FilterProvider>{children}</FilterProvider>
          </SaveFailedProvider>
        </ActivityLogsProvider>
      </UpdateTasksProvider>
    </SelectedForMergeProvider>
  </ExtraInfoProvider>
);

function renderReactRoot(children: React.ReactElement, selector: string) {
  const element = document.getElementById(selector);
  invariant(element);

  const { unleashConfig, faro } = getCvPartner();

  const root = createRoot(element, {
    onUncaughtError: (error, errorInfo) => {
      faro?.api.pushError(error as Error, {
        context: { componentStack: errorInfo.componentStack ?? "" },
      });
      Sentry.captureException(error, {
        extra: { componentStack: errorInfo.componentStack },
      });
    },
    onCaughtError: (error, errorInfo) => {
      faro?.api.pushError(error as Error, {
        context: { componentStack: errorInfo.componentStack ?? "" },
      });
      Sentry.captureException(error, {
        level: "warning",
        extra: { componentStack: errorInfo.componentStack },
      });
    },
  });

  root.render(
    <React.StrictMode>
      <FaroErrorBoundary>
        <FlagProvider
          config={unleashConfig}
          startClient={unleashConfig != null}
        >
          <QueryClientProvider>
            <NothingUntilUnleashReady>
              <I18nProvider locale={locale}>
                <Providers>
                  <TimeoutWatcher />
                  {children}
                </Providers>
              </I18nProvider>
            </NothingUntilUnleashReady>
            {isDevelopment && (
              <TanStackDevtools
                config={{ openHotkey: [] }}
                plugins={[
                  {
                    name: "TanStack Query",
                    render: <ReactQueryDevtoolsPanel />,
                  },
                ]}
              />
            )}
          </QueryClientProvider>
        </FlagProvider>
        <NavigationBlocker />
      </FaroErrorBoundary>
    </React.StrictMode>,
  );

  return root;
}

export function createRouter(
  routes: RouteObject[],
  useBrowserHistoryRouter = false,
): Router {
  const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(
    useBrowserHistoryRouter ? createBrowserRouter : createHashRouter,
  );

  let router = sentryCreateBrowserRouter(routes);

  const { faro } = getCvPartner();

  if (faro != null) {
    router = withFaroRouterInstrumentation(router);

    router.subscribe(() => {
      const currentHref = window.location.href;

      if (faro.api.getPage()?.url !== currentHref) {
        faro.api.setPage({
          url: currentHref,
          attributes: {
            host: window.location.host,
          },
        });
      }
    });
  }

  return router;
}

export function renderRouter(router: Router, selector: string) {
  renderReactRoot(<RouterProvider router={router} />, selector);
}
